/**
 * The website preview in the Workbench, and the Make changes mode it opens
 * (docs/website_make_changes.md). The mode itself runs inside the site preview
 * (WebsiteViewerPage `?make_changes=1`); this frame does what the transaction
 * mode does around a screen: the mode opens FULL SCREEN over the Workbench, its
 * bar's Restore puts it back in the pane, Done returns to the preview, and
 * ✦ Ask AI to change pre-fills the Ask-AI panel (never sends).
 *
 * The iframe element is the same in the pane and full screen — only the
 * wrapper's style changes — so going full screen never reloads the site.
 */

import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Z_FULLSCREEN_VIEW } from '@twasta/page-runtime'
import { prefillDock } from '@/store/dockBridgeStore'
import { readSiteEditMessage } from '@/pages/website/edit/frameBridge'

export default function SiteEditFrame({ siteUrl, title, editRequest, editPage, onModeEnd, onSiteChanged }: {
  /** `/site/<owner>/<project>/<site slug>` */
  siteUrl: string
  title: string
  /** A new number opens the mode (the frame reloads into it); 0 = preview. */
  editRequest: number
  /** The page the mode opens on (a page node's Make changes); none = the home page. */
  editPage?: string
  /** The mode closed (Done). */
  onModeEnd?: () => void
  /** Pages or website settings changed while editing (the tree may be out of date). */
  onSiteChanged?: () => void
}) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [full, setFull] = useState(false)
  const siteChanged = useRef(false)
  const onModeEndRef = useRef(onModeEnd)
  onModeEndRef.current = onModeEnd
  const onSiteChangedRef = useRef(onSiteChanged)
  onSiteChangedRef.current = onSiteChanged

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const msg = readSiteEditMessage(e, frameRef.current)
      if (!msg) return
      if (msg.event === 'fullscreen') setFull(msg.on)
      else if (msg.event === 'ask-ai') {
        // The frame steps back into its pane with it (a `fullscreen` message),
        // so the Ask-AI panel is in sight to send.
        prefillDock(msg.text)
      } else if (msg.event === 'site-changed') {
        siteChanged.current = true
      } else if (msg.event === 'mode' && !msg.on) {
        setFull(false)
        if (siteChanged.current) {
          siteChanged.current = false
          onSiteChangedRef.current?.()
        }
        onModeEndRef.current?.()
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Back to the plain preview: never leave the layer over the Workbench.
  useEffect(() => { if (!editRequest) setFull(false) }, [editRequest])

  const params = new URLSearchParams()
  if (editRequest) {
    params.set('make_changes', '1')
    if (editPage) params.set('page', editPage)
  }
  const src = siteUrl + (params.toString() ? `?${params}` : '')

  return (
    <div
      data-tw-screen={full ? 'site-edit-fullscreen' : undefined}
      style={full ? {
        position: 'fixed', inset: 0, zIndex: Z_FULLSCREEN_VIEW,
        display: 'flex', flexDirection: 'column', background: '#fff',
      } : {
        flex: 1, minHeight: 460,
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}
    >
      <iframe
        ref={frameRef}
        key={`${siteUrl}:${editRequest}:${editPage || ''}`}
        src={src}
        title={`Site preview: ${title}`}
        onError={() => toast.error('The website preview could not be loaded')}
        style={{ flex: 1, width: '100%', border: 'none', background: '#fff' }}
      />
    </div>
  )
}
